import { useQuery } from "@tanstack/react-query";

import type { NativeModuleProps } from "../registry";
import { gridQuery, metaOf, type FreshnessMeta } from "./api";
import { ConfigTab } from "./ConfigTab";
import { DiscountsTab } from "./DiscountsTab";
import { LogsTab } from "./LogsTab";
import { OverviewTab } from "./OverviewTab";
import { PriceRunTab } from "./PriceRunTab";
import { ShopifyEconomicsTab } from "./ShopifyEconomicsTab";
import { StrategiesTab } from "./StrategiesTab";
import { SubscribersTab } from "./SubscribersTab";

/**
 * The shell's freshness line. It reads the EU grid through the same query the tab uses (one cache entry).
 * Undefined while loading, on any error and when EU has no grid yet, so the shell shows its honest
 * "we can't confirm this data's date" line rather than implying freshness (I-6).
 */
export function usePrecisionPricingMeta(enabled: boolean): FreshnessMeta | null | undefined {
  const head = useQuery({ ...gridQuery("EU"), enabled });
  if (!enabled || !head.data || head.error) return undefined;
  return metaOf(head.data.envelope);
}

export function PrecisionPricingModule({ activeTabKey, areaName }: NativeModuleProps) {
  // Shopify Economics is live from C1 v1.2.
  if (activeTabKey === "shopify_economics") return <ShopifyEconomicsTab areaName={areaName} />;
  // Overview is live from C1 v1.3 (sections 1, 2 and 5; sections 3 and 4 are release cards inside the tab).
  if (activeTabKey === "overview") return <OverviewTab areaName={areaName} />;
  // Logs is live from C1 v1.4 (Costs and Price panes; the RRP exceptions pane from C1 v1.6).
  if (activeTabKey === "logs") return <LogsTab areaName={areaName} />;
  // Config is live from C1 v1.5 (every value reads "not set" until it is set; currency links read-only).
  if (activeTabKey === "config") return <ConfigTab areaName={areaName} />;
  // Strategies is live from C1 v1.7 and an editor from C1 v1.15 (the map by dropdown, the rule library, the strategy cards).
  if (activeTabKey === "strategies") return <StrategiesTab areaName={areaName} />;
  // Discounts is live from C1 v1.8 and an editor from C1 v1.22 (the Subscriptions and Distribution Partners panes, item5).
  if (activeTabKey === "discounts") return <DiscountsTab areaName={areaName} />;
  // Subscribers is live from C1 v1.33 (N9a release 1: the Price changes and Drift panes, read-only).
  if (activeTabKey === "subscribers") return <SubscribersTab areaName={areaName} />;
  // Every tab of the shell's bar is live (C1 v1.31 removed the release card); any other key opens the Price Run.
  return <PriceRunTab areaName={areaName} />;
}
